import { Search, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { departmentService } from '../api/services/department.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { DepartmentTreeRows } from '../components/departments/DepartmentTreeRows'
import { DepartmentParentPicker } from '../components/departments/DepartmentParentPicker'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { useAuth } from '../context/AuthContext'
import {
  collectDepartmentDescendantIds,
  countDepartmentNodes,
  findDepartmentInTree,
} from '../lib/department-tree'
import type { Department, DepartmentFormPayload } from '../types/department'

type ActiveFilter = 'all' | 'active' | 'disabled'
const ACTIVE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const departmentSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  parent_id: z.string().optional(),
  is_active: z.boolean(),
})

type DepartmentFormValues = z.infer<typeof departmentSchema>

const emptyForm: DepartmentFormValues = {
  name: '',
  parent_id: '',
  is_active: true,
}

export function DepartmentsPage() {
  const { can } = useAuth()
  const [tree, setTree] = useState<Department[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Department | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null)
  const [deleting, setDeleting] = useState(false)

  const form = useForm<DepartmentFormValues>({
    resolver: zodResolver(departmentSchema),
    defaultValues: emptyForm,
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (activeFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [activeFilter, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setActiveFilter('all')
    setWithTrashed(false)
  }

  const loadTree = useCallback(async () => {
    try {
      const response = await departmentService.tree({
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      })
      setTree(Array.isArray(response.data) ? response.data : [])
    } catch {
      setTree([])
    } finally {
      setHasLoaded(true)
    }
  }, [activeFilter, search, withTrashed])

  useEffect(() => {
    void loadTree()
  }, [loadTree])

  const excludedParentIds = useMemo(() => {
    if (!editing) {
      return new Set<string>()
    }

    const node = findDepartmentInTree(tree, editing.id)

    if (!node) {
      return new Set([editing.id])
    }

    return new Set([editing.id, ...collectDepartmentDescendantIds(node)])
  }, [editing, tree])

  const openCreate = (parent?: Department) => {
    setEditing(null)
    form.reset({
      ...emptyForm,
      parent_id: parent?.id ?? '',
    })
    setFormOpen(true)
  }

  const openEdit = (department: Department) => {
    setEditing(department)
    form.reset({
      name: department.name,
      parent_id: department.parent_id ?? '',
      is_active: department.is_active,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    form.reset(emptyForm)
  }

  const onSubmit = async (values: DepartmentFormValues) => {
    setSubmitting(true)

    const payload: DepartmentFormPayload = {
      name: values.name,
      parent_id: values.parent_id || null,
      is_active: values.is_active,
    }

    try {
      if (editing) {
        await departmentService.update(editing.id, payload)
      } else {
        await departmentService.create(payload)
      }

      closeForm()
      await loadTree()
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await departmentService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadTree()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (department: Department) => {
    try {
      await departmentService.restore(department.id)
      await loadTree()
    } catch {
      // handled globally
    }
  }

  const totalCount = countDepartmentNodes(tree)

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Departments</h1>
            <p className="mt-1 text-sm text-ink-600">
              Manage unlimited sub-department levels — each department can have many child
              departments, nested as deep as needed.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>

            <PermissionGate permission="departments-create">
              <Button onClick={() => openCreate()}>Add Department</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Name</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Search department name…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSearch(searchInput.trim())
                    }
                  }}
                />
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACTIVE_OPTIONS}
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as ActiveFilter)}
                placeholder="All"
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Department tree</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && totalCount === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-sm text-ink-500">
                      No departments found.
                    </td>
                  </tr>
                ) : (
                  <DepartmentTreeRows
                    nodes={tree}
                    canUpdate={can('departments-update')}
                    canDelete={can('departments-delete')}
                    canRestore={can('departments-restore')}
                    canCreate={can('departments-create')}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onRestore={(department) => void handleRestore(department)}
                    onAddSubDepartment={(parent) => openCreate(parent)}
                  />
                )}
              </tbody>
            </table>
          </div>
        </section>

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="department"
          itemName={deleteTarget?.name ?? ''}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">
                {editing ? 'Edit Department' : 'Create Department'}
              </h2>
              <p className="mt-1 text-sm text-ink-600">
                Choose a parent to nest under, or leave as top-level.
              </p>

              <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <FieldLabel optional>Parent department</FieldLabel>
                  <Controller
                    control={form.control}
                    name="parent_id"
                    render={({ field }) => (
                      <DepartmentParentPicker
                        tree={tree}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        excludedIds={excludedParentIds}
                      />
                    )}
                  />
                  <p className="mt-1 text-xs text-ink-500">
                    Hover a department to expand its sub-departments, then click to select.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register('is_active')} />
                  Active
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Department'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
